import type { ReactNode } from 'react';
import { CameraIcon } from '../../icons';

/**
 * 시안 `.btn-first-photo` — 빈 상태의 단일 CTA("첫 사진 추가하기").
 *
 * 빈 상태에서 유일하게 누를 것이라 전역 `Button`의 primary보다 한 단계 크고 밝다.
 * 호출부(데스크톱·폴드)가 각자 `Button`을 넘기면 결이 갈리므로 클래스를 여기서 소유한다.
 */
export const FIRST_PHOTO_BUTTON_CLASS =
  'inline-flex h-11 items-center gap-2 rounded-card bg-gradient-to-br from-brand-600 to-brand-500 px-[22px] text-[15px] font-bold tracking-[-0.01em] text-white shadow-[0_8px_18px_-6px_rgba(79,70,229,0.5)] transition enabled:hover:-translate-y-px enabled:hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none';

interface PhotoEmptyStateProps {
  /** 굵은 한 줄. 시안 `.empty-text`. */
  title: string;
  /** 보조 설명. 시안 `.empty-sub`. 안내만 있고 액션이 없는 경우에도 이 자리를 쓴다. */
  description?: ReactNode;
  /** 하단 액션. 시안 결은 `FIRST_PHOTO_BUTTON_CLASS`를 쓴다. 없으면 렌더하지 않는다. */
  action?: ReactNode;
  /**
   * 세로 여백. 데스크톱은 시안 `.insp-empty`의 `padding:90px 20px`를 그대로 쓰고,
   * 폴드/좁은 폭에서는 화면 대부분을 빈 상자가 차지해 버려 `compact`로 줄인다.
   */
  density?: 'default' | 'compact';
}

const DENSITY_CLASS = {
  default: 'px-5 py-[90px]',
  compact: 'px-5 py-12',
} as const;

/**
 * 사진 빈 상태 — 시안 `.insp-empty`의 단일 소스.
 *
 * 데스크톱(`InspectionPhotoSection`)과 폴드(`InspectionDetailFold`)가 같은 화면의 같은
 * 상태를 각자 그리고 있어서, 한쪽만 고치면 기기별로 다른 빈 상태가 남는다. 실제로 두 곳이
 * 이미 여백(`p-12` vs `p-8`)과 문구 유무로 갈려 있었다.
 *
 * 일러스트 타일의 amber 액센트 원(`.empty-illus::after`)은 한때 "빈 상태에서 유일하게
 * 눈에 띄는 색이 amber면 경고로 읽힌다"는 이유로 빼 두었으나, 2026-08-02 사용자가 시안을
 * 채택하기로 결정해 복원했다. 원은 `aria-hidden` 타일 안의 순수 장식이라 의미를 나르지 않는다.
 */
export function PhotoEmptyState({
  title,
  description,
  action,
  density = 'default',
}: PhotoEmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-card-lg border border-dashed border-line bg-white text-center shadow-card-sm ${DENSITY_CLASS[density]}`}
    >
      <div
        aria-hidden="true"
        className="relative mb-[18px] grid h-[84px] w-[84px] place-items-center rounded-[22px] border border-brand-100 bg-gradient-to-br from-brand-50 to-white text-brand-500"
      >
        <CameraIcon className="h-10 w-10" />
        {/* 시안 `.empty-illus::after` — 타일에 걸친 amber 원. 흰 테두리가 타일 경계를 끊어
            "붙어 있는 배지"로 읽히게 한다(바탕이 흰 카드라 border-white가 맞다). */}
        <span className="absolute -bottom-1.5 -right-1.5 h-[30px] w-[30px] rounded-full border-[3px] border-white bg-gradient-to-br from-amber-400 to-amber-600" />
      </div>

      <p className="mb-1.5 text-[17px] font-bold tracking-[-0.015em] text-ink-700">{title}</p>
      {description && (
        <p className="mb-[22px] text-[13.5px] font-medium text-ink-500">{description}</p>
      )}

      {action}
    </div>
  );
}
